import React, { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget. Calls onToken(token|null) when challenge completes/expires.
 * Renders nothing when siteKey is empty (tests / misconfig with skip-verify on server).
 *
 * Pass a changing ``resetKey`` after a failed submit so a fresh token is required.
 */
export function TurnstileWidget({ siteKey, onToken, className, resetKey = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep latest callback without re-rendering the widget on every parent render.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      onTokenRef.current?.(null);
      return undefined;
    }

    let cancelled = false;

    const emit = (token) => {
      if (!cancelled) onTokenRef.current?.(token);
    };

    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => emit(token),
        'expired-callback': () => emit(null),
        'error-callback': () => emit(null),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      const existing = document.querySelector('script[data-hireiq-turnstile]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.dataset.hireiqTurnstile = '1';
        script.onload = () => render();
        document.head.appendChild(script);
      } else if (window.turnstile) {
        render();
      } else {
        existing.addEventListener('load', render);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, resetKey]);

  if (!siteKey) return null;

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid="turnstile-widget"
    />
  );
}

export function passwordMeetsPolicy(password) {
  if (!password || password.length < 8) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!/[^A-Za-z0-9]/.test(password)) return false;
  return true;
}

export const PASSWORD_POLICY_HINT =
  'At least 8 characters, with uppercase, lowercase, a number, and a special character.';
